// Translation dictionary for MealMatcher marketing site.
// Default language is Norwegian (no), with English (en) as a toggle option.

export const translations = {
  no: {
    nav: {
      features: 'Funksjoner',
      gallery: 'Galleri',
      faq: 'Spørsmål',
      comingSoon: 'Kommer snart',
    },
    hero: {
      badge: 'Lanseres snart',
      titleLine1: 'Slutt å lure på',
      titleLine2: 'hva du skal lage.',
      scribble: 'finn din neste favoritt',
      subtitle:
        'MealMatcher hjelper deg å oppdage oppskrifter du faktisk vil lage, planlegge uka og lage handleliste — på minutter, ikke timer.',
      ctaPrimary: 'Bli varslet ved lansering',
      ctaSecondary: 'Se hvordan det funker',
      stat1: 'Sveip & match',
      stat2: 'Planlegg uka',
      stat3: 'Smart handleliste',
      comingSoonAppStore: 'Snart i App Store',
      comingSoonPlayStore: 'Snart på Google Play',
    },
    features: {
      eyebrow: 'En app, hele måltidsreisen',
      title: 'Fra inspirasjon til middagsbord.',
      subtitle:
        'Alt du trenger for å spise bedre — pakket inn i en lekker, lekker opplevelse.',
      list: {
        match: {
          title: 'Meal Match',
          desc: 'Algoritmen lærer hva du liker. Sveip deg gjennom oppskrifter laget for akkurat din smak.',
          tag: 'Personlig',
        },
        discover: {
          title: 'Oppdag & filtrer',
          desc: 'Filtrer på type, pris og tid. Finn middag på under 30 minutter eller en helgens prosjekt.',
          tag: 'Smart søk',
        },
        planner: {
          title: 'Måltidsplanlegger',
          desc: 'Dra og slipp oppskrifter inn i en visuell kalender. Hele uka planlagt på minutter.',
          tag: 'Kalender',
        },
        list: {
          title: 'Smart handleliste',
          desc: 'Handlelisten lages automatisk fra planlagte måltider. Rediger, bytt ut og kryss av.',
          tag: 'Automatisk',
        },
        nutrition: {
          title: 'Næring & porsjoner',
          desc: 'Se kalorier, protein, fett og fiber. Skaler oppskriften opp eller ned med ett trykk.',
          tag: 'Helse',
        },
        favorites: {
          title: 'Likt & mislikt',
          desc: 'Marker oppskrifter du elsker eller vil unngå. MealMatcher husker det neste gang.',
          tag: 'Læring',
        },
      },
    },
    gallery: {
      eyebrow: 'Et kikk inn i appen',
      title: 'Lag-deg-mat har aldri sett bedre ut.',
    },
    faq: {
      title: 'Ofte stilte spørsmål',
      subtitle: 'Alt du lurer på før lansering.',
      items: [
        {
          q: 'Når kan jeg laste ned MealMatcher?',
          a: 'Vi lanserer i App Store og Google Play snart. Meld deg på varslinger så får du beskjed først.',
        },
        {
          q: 'Koster appen noe?',
          a: 'MealMatcher blir gratis å laste ned, med en mulighet for premium-funksjoner senere.',
        },
        {
          q: 'Hvilke språk støttes?',
          a: 'Appen lanseres med norsk og engelsk ved start. Flere språk er på veien.',
        },
        {
          q: 'Kan jeg justere oppskrifter etter antall porsjoner?',
          a: 'Ja — alle oppskrifter kan skaleres opp og ned, og ingrediensmengder oppdateres automatisk.',
        },
        {
          q: 'Tar dere hensyn til allergier og kosthold?',
          a: 'Du kan markere oppskrifter du ikke ønsker, og algoritmen lærer av valgene dine over tid.',
        },
        {
          q: 'Hvordan melder jeg meg av e-postene?',
          a: 'Alle e-poster vi sender har en avmeldingslenke nederst — ett klikk er nok. Gmail og Apple Mail viser også en innebygd "Meld av"-knapp øverst i e-posten. Trenger du hjelp? Skriv til corporate@mealmatcher.no.',
        },
        {
          q: 'Hvordan kommer jeg i kontakt med dere?',
          a: 'Send oss en e-post på corporate@mealmatcher.no — vi svarer som regel innen 24 timer.',
        },
      ],
    },
    cta: {
      title: 'Klar for å spise bedre?',
      subtitle: 'Bli først til å vite når MealMatcher lanseres.',
      button: 'Hold meg oppdatert',
    },
    footer: {
      tag: 'Lager den deiligste måten å planlegge måltidene dine.',
      contact: 'Kontakt',
      follow: 'Følg oss',
      copyright: '© 2025 MealMatcher. Laget med kjærlighet i Norge.',
      links: {
        privacy: 'Personvern',
        terms: 'Vilkår',
      },
    },
    legal: {
      back: 'Tilbake til forsiden',
      lastUpdated: 'Sist oppdatert: desember 2025',
      privacy: {
        title: 'Personvernerklæring',
        intro:
          'MealMatcher tar ditt personvern på alvor. Denne erklæringen forklarer hvordan vi samler inn, bruker og beskytter personopplysningene dine når du bruker nettsiden mealmatcher.no.',
        sections: [
          {
            h: '1. Behandlingsansvarlig',
            p: 'MealMatcher (org.nr. 928 846 288), Skokkabakken 2 B, 4330 Ålgård, Norge, er behandlingsansvarlig for personopplysningene som samles inn via mealmatcher.no. Kontakt: <a class="text-orange-600 underline" href="mailto:corporate@mealmatcher.no">corporate@mealmatcher.no</a>.',
          },
          {
            h: '2. Hvilke opplysninger vi samler inn',
            p: 'Denne nettsiden er en informasjonsside for vår kommende mobilapp. Vi samler inn følgende personopplysninger:<br/><br/><strong>Påmelding til ventelisten:</strong> e-postadresse, valgt språk (norsk eller engelsk), tidspunkt for påmelding, og IP-adresse i form av aggregerte logger fra hostingleverandøren vår.<br/><br/>Vi samler ikke inn navn, fødselsdato, posisjon eller annen identifiserende informasjon på dette tidspunktet. Når mobilappen lanseres vil vi oppdatere denne erklæringen med informasjon om hvilke data appen behandler.',
          },
          {
            h: '3. Formål og rettslig grunnlag',
            p: 'Vi behandler e-postadressen din for å:<br/>• sende deg bekreftelse på påmeldingen,<br/>• varsle deg når appen lanseres,<br/>• gi deg eventuelle viktige oppdateringer om lanseringen.<br/><br/>Det rettslige grunnlaget for behandlingen er ditt samtykke (GDPR art. 6(1)(a)). Du kan når som helst trekke samtykket tilbake.',
          },
          {
            h: '4. Lagringstid',
            p: 'E-postadressen din lagres frem til appen lanseres og du har mottatt lanseringsvarsel, eller til du ber om å bli slettet — det som inntreffer først. Etter lansering kan vi flytte påmeldingen din inn i appens brukerdatabase dersom du velger å opprette en konto.',
          },
          {
            h: '5. Mottakere og databehandlere',
            p: 'Vi deler e-postadressen din kun med følgende databehandlere som leverer tjenester på våre vegne:<br/>• <strong>Resend</strong> (USA/EU) — sender bekreftelses- og lanseringsvarsel-e-poster.<br/>• <strong>MongoDB-hosting</strong> — lagrer ventelisten sikkert.<br/><br/>Vi selger ikke e-postadresser, og vi bruker dem ikke til markedsføring fra tredjepart.',
          },
          {
            h: '6. Overføring utenfor EØS',
            p: 'Enkelte av våre databehandlere er etablert utenfor EØS. Overføringer skjer på grunnlag av EU-kommisjonens standardkontrakter (SCC) og DPF der det er aktuelt.',
          },
          {
            h: '7. Dine rettigheter',
            p: 'Du har rett til å:<br/>• be om innsyn i hvilke opplysninger vi har om deg,<br/>• be om retting eller sletting,<br/>• be om at behandlingen begrenses eller opphører,<br/>• kreve dataportabilitet,<br/>• klage til Datatilsynet (datatilsynet.no).<br/><br/>For å utøve rettighetene dine, send oss en e-post på <a class="text-orange-600 underline" href="mailto:corporate@mealmatcher.no">corporate@mealmatcher.no</a>.',
          },
          {
            h: '8. Sikkerhet',
            p: 'Vi benytter tekniske og organisatoriske tiltak for å beskytte dine personopplysninger mot uautorisert tilgang, endring eller sletting. All trafikk til mealmatcher.no krypteres via HTTPS.',
          },
          {
            h: '9. Informasjonskapsler',
            p: 'Denne nettsiden bruker ingen sporings-informasjonskapsler eller tredjeparts analyseverktøy. Nettleseren din kan lagre nødvendige tekniske informasjonskapsler for at siden skal fungere.',
          },
          {
            h: '10. Endringer',
            p: 'Vi kan oppdatere denne personvernerklæringen ved behov. Vesentlige endringer vil bli varslet på denne siden.',
          },
        ],
      },
      terms: {
        title: 'Vilkår for bruk',
        intro:
          'Disse vilkårene gjelder bruk av nettsiden mealmatcher.no og påmelding til ventelisten for vår kommende mobilapp.',
        sections: [
          {
            h: '1. Om tjenesten',
            p: 'mealmatcher.no er en informasjonsside drevet av MealMatcher (org.nr. 928 846 288), Skokkabakken 2 B, 4330 Ålgård, Norge. Nettsiden inneholder informasjon om vår kommende mobilapp og lar deg melde deg på en venteliste for å bli varslet om lanseringen.',
          },
          {
            h: '2. Aksept av vilkår',
            p: 'Ved å bruke nettsiden eller melde deg på ventelisten, aksepterer du disse vilkårene. Hvis du ikke aksepterer vilkårene, ber vi deg om å ikke bruke nettsiden.',
          },
          {
            h: '3. Ventelisten',
            p: 'Påmelding til ventelisten er gratis og innebærer ingen kjøps- eller bindingsforpliktelse. Du kan når som helst be om å bli fjernet ved å sende en e-post til <a class="text-orange-600 underline" href="mailto:corporate@mealmatcher.no">corporate@mealmatcher.no</a>.',
          },
          {
            h: '4. Korrekt e-postadresse',
            p: 'Du forplikter deg til kun å oppgi en e-postadresse du selv har kontroll over. Påmelding på vegne av andre uten deres samtykke er ikke tillatt.',
          },
          {
            h: '5. Immaterielle rettigheter',
            p: 'Alt innhold på mealmatcher.no — inkludert tekst, bilder, logoer, design og kildekode — tilhører MealMatcher eller våre lisensgivere, og er beskyttet av åndsverkloven. Innholdet kan ikke kopieres, distribueres eller brukes kommersielt uten skriftlig samtykke.',
          },
          {
            h: '6. Ansvarsbegrensning',
            p: 'Nettsiden leveres "som den er". Vi gir ingen garantier for tilgjengelighet, nøyaktighet eller egnethet til et bestemt formål. MealMatcher er ikke ansvarlig for indirekte tap som måtte oppstå ved bruk av nettsiden eller informasjonen på den.',
          },
          {
            h: '7. Endringer i tjenesten',
            p: 'Vi forbeholder oss retten til å endre eller avslutte nettsiden, ventelisten eller mobilappen — helt eller delvis — uten forvarsel.',
          },
          {
            h: '8. Lovvalg og verneting',
            p: 'Disse vilkårene reguleres av norsk rett. Eventuelle tvister skal avgjøres av norske domstoler, med Stavanger tingrett som verneting.',
          },
          {
            h: '9. Kontakt',
            p: 'Spørsmål om disse vilkårene kan rettes til <a class="text-orange-600 underline" href="mailto:corporate@mealmatcher.no">corporate@mealmatcher.no</a>.',
          },
        ],
      },
    },
    waitlist: {
      placeholder: 'Din e-postadresse',
      button: 'Bli med',
      loading: 'Sender...',
      legal: 'Vi sender bare beskjed når appen lanseres. Ingen spam — løfte.',
      successTitle: 'Du står på listen! 🎉',
      successBody: 'Sjekk innboksen din for en bekreftelse fra MealMatcher.',
      alreadyTitle: 'Du er allerede påmeldt 👋',
      alreadyBody: 'Vi gir beskjed så snart appen er klar.',
      invalid: 'Vennligst skriv inn en gyldig e-postadresse.',
      errorGeneric: 'Noe gikk galt. Prøv igjen om et øyeblikk.',
      rateLimited: 'For mange forsøk. Vent et minutt og prøv igjen.',
    },
    unsubscribe: {
      working: 'Melder deg av...',
      successTitle: 'Du er meldt av',
      successBody:
        'Vi har fjernet e-postadressen din fra MealMatcher-listen. Du vil ikke motta flere e-poster fra oss.',
      alreadyTitle: 'Allerede meldt av',
      alreadyBody: 'Denne e-postadressen står ikke lenger på listen.',
      errorTitle: 'Lenken er ugyldig',
      errorBody: 'Avmeldingslenken er ugyldig eller utløpt. Send oss en e-post på corporate@mealmatcher.no så fjerner vi deg manuelt.',
      missingTitle: 'Manglende informasjon',
      missingBody: 'Avmeldingslenken er ufullstendig. Sjekk at du har åpnet den fullstendige lenken fra e-posten.',
      backHome: 'Tilbake til forsiden',
      rejoin: 'Ombestemt deg? Meld deg på igjen',
    },
    store: {
      appStoreLive: 'Last ned i App Store',
      playStoreLive: 'Hent på Google Play',
    },
  },
  en: {
    nav: {
      features: 'Features',
      gallery: 'Gallery',
      faq: 'FAQ',
      comingSoon: 'Coming soon',
    },
    hero: {
      badge: 'Launching soon',
      titleLine1: 'Stop wondering',
      titleLine2: 'what to cook.',
      scribble: 'find your next favourite',
      subtitle:
        'MealMatcher helps you discover recipes you actually want to cook, plan your week, and build a shopping list — in minutes, not hours.',
      ctaPrimary: 'Notify me at launch',
      ctaSecondary: 'See how it works',
      stat1: 'Swipe & match',
      stat2: 'Plan the week',
      stat3: 'Smart shopping list',
      comingSoonAppStore: 'Soon on App Store',
      comingSoonPlayStore: 'Soon on Google Play',
    },
    features: {
      eyebrow: 'One app, the whole meal journey',
      title: 'From inspiration to dinner table.',
      subtitle:
        'Everything you need to eat better — wrapped in a delightful, polished experience.',
      list: {
        match: {
          title: 'Meal Match',
          desc: 'The algorithm learns what you love. Swipe through recipes built around your taste.',
          tag: 'Personal',
        },
        discover: {
          title: 'Discover & filter',
          desc: 'Filter by dish type, cost and prep time. Find dinner in under 30 mins or a weekend project.',
          tag: 'Smart search',
        },
        planner: {
          title: 'Meal planner',
          desc: 'Drag recipes into a visual calendar. Plan the entire week in minutes.',
          tag: 'Calendar',
        },
        list: {
          title: 'Smart shopping list',
          desc: 'A shopping list is generated automatically from your planned meals. Edit, swap and check off.',
          tag: 'Automatic',
        },
        nutrition: {
          title: 'Nutrition & servings',
          desc: 'See calories, protein, fat and fibre. Scale any recipe up or down with one tap.',
          tag: 'Healthy',
        },
        favorites: {
          title: 'Liked & disliked',
          desc: 'Mark recipes you love or want to avoid. MealMatcher remembers next time around.',
          tag: 'Learning',
        },
      },
    },
    gallery: {
      eyebrow: 'A peek inside the app',
      title: 'Cooking has never looked better.',
    },
    faq: {
      title: 'Frequently asked questions',
      subtitle: 'Everything you might want to know before launch.',
      items: [
        {
          q: 'When can I download MealMatcher?',
          a: 'We are launching on the App Store and Google Play soon. Sign up for notifications and we will tell you first.',
        },
        {
          q: 'Will the app cost anything?',
          a: 'MealMatcher will be free to download, with optional premium features later.',
        },
        {
          q: 'Which languages are supported?',
          a: 'The app launches with Norwegian and English. More languages are on the way.',
        },
        {
          q: 'Can I adjust recipes by serving size?',
          a: 'Yes — every recipe scales up or down, and ingredient quantities update automatically.',
        },
        {
          q: 'Do you consider allergies and diet preferences?',
          a: 'You can mark recipes you do not want, and the algorithm learns from your choices over time.',
        },
        {
          q: 'How do I unsubscribe from your emails?',
          a: 'Every email we send has a one-click unsubscribe link at the bottom. Gmail and Apple Mail also show a native "Unsubscribe" button at the top of the email. Need help? Email corporate@mealmatcher.no.',
        },
        {
          q: 'How do I get in touch with you?',
          a: 'Send us an email at corporate@mealmatcher.no — we usually reply within 24 hours.',
        },
      ],
    },
    cta: {
      title: 'Ready to eat better?',
      subtitle: 'Be the first to know when MealMatcher launches.',
      button: 'Keep me posted',
    },
    footer: {
      tag: 'Cooking up the tastiest way to plan your meals.',
      contact: 'Contact',
      follow: 'Follow us',
      copyright: '© 2025 MealMatcher. Made with love in Norway.',
      links: {
        privacy: 'Privacy',
        terms: 'Terms',
      },
    },
    legal: {
      back: 'Back to home',
      lastUpdated: 'Last updated: December 2025',
      privacy: {
        title: 'Privacy Policy',
        intro:
          'MealMatcher takes your privacy seriously. This policy explains how we collect, use and protect your personal data when you use the mealmatcher.no website.',
        sections: [
          {
            h: '1. Data controller',
            p: 'MealMatcher (organisation number 928 846 288), Skokkabakken 2 B, 4330 Ålgård, Norway, is the data controller for personal data collected via mealmatcher.no. Contact: <a class="text-orange-600 underline" href="mailto:corporate@mealmatcher.no">corporate@mealmatcher.no</a>.',
          },
          {
            h: '2. What we collect',
            p: 'This website is an information page for our upcoming mobile app. We collect the following personal data:<br/><br/><strong>Waitlist sign-up:</strong> email address, chosen language (Norwegian or English), sign-up timestamp, and IP addresses in the form of aggregated server logs from our hosting provider.<br/><br/>We do not collect names, dates of birth, location or other identifying information at this stage. When the mobile app launches we will update this policy with details of what the app processes.',
          },
          {
            h: '3. Purpose and legal basis',
            p: 'We process your email address to:<br/>• send you a sign-up confirmation,<br/>• notify you when the app launches,<br/>• share any important launch-related updates.<br/><br/>The legal basis for the processing is your consent (GDPR art. 6(1)(a)). You may withdraw consent at any time.',
          },
          {
            h: '4. Retention',
            p: 'Your email address is stored until the app launches and you have received the launch notification, or until you ask to be deleted — whichever happens first. After launch, we may migrate your sign-up into the app\'s user database if you choose to create an account.',
          },
          {
            h: '5. Recipients and processors',
            p: 'We share your email address only with the following data processors providing services on our behalf:<br/>• <strong>Resend</strong> (USA/EU) — sends confirmation and launch notification emails.<br/>• <strong>MongoDB hosting</strong> — securely stores the waitlist.<br/><br/>We do not sell email addresses and we do not use them for third-party marketing.',
          },
          {
            h: '6. Transfers outside the EEA',
            p: 'Some of our processors are established outside the EEA. Transfers happen on the basis of the European Commission\'s Standard Contractual Clauses (SCCs) and the EU-US Data Privacy Framework where applicable.',
          },
          {
            h: '7. Your rights',
            p: 'You have the right to:<br/>• request access to the data we hold about you,<br/>• request correction or deletion,<br/>• request that processing be restricted or stopped,<br/>• request data portability,<br/>• file a complaint with the Norwegian Data Protection Authority (datatilsynet.no).<br/><br/>To exercise your rights, email us at <a class="text-orange-600 underline" href="mailto:corporate@mealmatcher.no">corporate@mealmatcher.no</a>.',
          },
          {
            h: '8. Security',
            p: 'We use technical and organisational measures to protect your personal data against unauthorised access, alteration or deletion. All traffic to mealmatcher.no is encrypted via HTTPS.',
          },
          {
            h: '9. Cookies',
            p: 'This website does not use tracking cookies or third-party analytics. Your browser may store essential technical cookies that are necessary for the site to function.',
          },
          {
            h: '10. Changes',
            p: 'We may update this Privacy Policy from time to time. Material changes will be announced on this page.',
          },
        ],
      },
      terms: {
        title: 'Terms of Use',
        intro:
          'These terms govern your use of the mealmatcher.no website and sign-up to the waitlist for our upcoming mobile app.',
        sections: [
          {
            h: '1. About the service',
            p: 'mealmatcher.no is an information website operated by MealMatcher (organisation number 928 846 288), Skokkabakken 2 B, 4330 Ålgård, Norway. The website contains information about our upcoming mobile app and lets you sign up to a waitlist to be notified about the launch.',
          },
          {
            h: '2. Acceptance of terms',
            p: 'By using the website or signing up to the waitlist, you accept these terms. If you do not accept the terms, please do not use the website.',
          },
          {
            h: '3. The waitlist',
            p: 'Signing up to the waitlist is free and does not constitute a purchase or any binding obligation. You may ask to be removed at any time by emailing <a class="text-orange-600 underline" href="mailto:corporate@mealmatcher.no">corporate@mealmatcher.no</a>.',
          },
          {
            h: '4. Accurate email address',
            p: 'You agree to provide only an email address that you control. Signing up on behalf of others without their consent is not allowed.',
          },
          {
            h: '5. Intellectual property',
            p: 'All content on mealmatcher.no — including text, images, logos, design and source code — belongs to MealMatcher or our licensors and is protected by copyright law. Content may not be copied, distributed or used commercially without written consent.',
          },
          {
            h: '6. Limitation of liability',
            p: 'The website is provided "as is". We make no warranties regarding availability, accuracy or fitness for a particular purpose. MealMatcher is not liable for indirect losses arising from use of the website or the information on it.',
          },
          {
            h: '7. Service changes',
            p: 'We reserve the right to modify or discontinue the website, the waitlist or the mobile app — in whole or in part — without prior notice.',
          },
          {
            h: '8. Governing law and venue',
            p: 'These terms are governed by Norwegian law. Any disputes shall be settled by the Norwegian courts, with Stavanger District Court as venue.',
          },
          {
            h: '9. Contact',
            p: 'Questions about these terms can be sent to <a class="text-orange-600 underline" href="mailto:corporate@mealmatcher.no">corporate@mealmatcher.no</a>.',
          },
        ],
      },
    },
    waitlist: {
      placeholder: 'Your email address',
      button: 'Join',
      loading: 'Sending...',
      legal: 'We only email when the app launches. No spam — promise.',
      successTitle: "You're on the list! 🎉",
      successBody: 'Check your inbox for a confirmation from MealMatcher.',
      alreadyTitle: "You're already signed up 👋",
      alreadyBody: 'We will let you know as soon as the app is ready.',
      invalid: 'Please enter a valid email address.',
      errorGeneric: 'Something went wrong. Please try again in a moment.',
      rateLimited: 'Too many attempts. Please wait a minute and try again.',
    },
    unsubscribe: {
      working: 'Unsubscribing...',
      successTitle: "You're unsubscribed",
      successBody:
        'We have removed your email from the MealMatcher list. You will not receive further emails from us.',
      alreadyTitle: 'Already unsubscribed',
      alreadyBody: 'This email address is no longer on the list.',
      errorTitle: 'Invalid link',
      errorBody: 'The unsubscribe link is invalid or expired. Email us at corporate@mealmatcher.no and we will remove you manually.',
      missingTitle: 'Missing information',
      missingBody: 'The unsubscribe link is incomplete. Please make sure you opened the full link from the email.',
      backHome: 'Back to homepage',
      rejoin: 'Changed your mind? Sign up again',
    },
    store: {
      appStoreLive: 'Download on App Store',
      playStoreLive: 'Get it on Google Play',
    },
  },
};

export const defaultLang = 'no';
